import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Loader2, RotateCcw } from "lucide-react";
import { api } from "@/api";
import { logDiag, rememberTraced, stateSummary } from "@/lib/diagnostics";
import { toast } from "sonner";
import { formatTime } from "@/utils";

/**
 * MissedOccurrencesDialog — surfaces the individual missed beats of ONE recurring task so the
 * user can dismiss / mark-done each one without them cluttering the main list. The overdue
 * section only renders ONE grouped row per task; this dialog is the "expand" for that row.
 *
 * Contract:
 *   · `task._missedOccurrences` — array of ISO YYYY-MM-DD strings (oldest first).
 *   · Each tap on a row PATCHes the task's `completions[]` (union with the tapped ISO) and
 *     mutates the local state via `onOccurrenceCompleted(taskId, iso)` so the caller can
 *     update its own tasks[] cache — SSE will echo the same change for other devices.
 *   · Toggling on a done row REMOVES the ISO from completions[] (undo the mark).
 */
const isoToLabel = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
};

export const MissedOccurrencesDialog = ({ task, onClose, onCompletionsChanged }) => {
    const [busyIso, setBusyIso] = useState(null);
    // Local scratch copy of completions[] so successive taps feel instant. Seeded from the
    // parent's task on every mount — parent should re-mount by keying on task?.id if it wants
    // to reset.
    const [localCompletions, setLocalCompletions] = useState(() => task?.completions || []);
    const [localCompletionTimes, setLocalCompletionTimes] = useState(() => task?.completion_times || {});
    if (!task) return null;

    const missed = (task._missedOccurrences || []).filter((iso) => !localCompletions.includes(iso));
    const doneFromThisSession = localCompletions.filter((iso) => (task._missedOccurrences || []).includes(iso));

    const toggle = async (iso, currentlyDone) => {
        setBusyIso(iso);
        const nextCompletions = currentlyDone
            ? localCompletions.filter((x) => x !== iso)
            : Array.from(new Set([...localCompletions, iso]));
        // Timestamp bookkeeping: record NOW when a beat is marked done; strip it on undo so
        // the history row falls back to "Completed" without a stale HH:MM.
        const nextCompletionTimes = { ...localCompletionTimes };
        if (currentlyDone) {
            delete nextCompletionTimes[iso];
        } else {
            nextCompletionTimes[iso] = Date.now();
        }
        try {
            const traceId = api.newTraceId();
            logDiag({ kind: "complete.click", task_id: task.id, iso, view: "missed-dialog", trace_id: traceId, message: `"${String(task.text || "").slice(0, 60)}" ${currentlyDone ? "reopen" : "complete"}`, state: stateSummary(task) });
            rememberTraced(task.id, iso, task.text);
            const upd = currentlyDone ? await api.reopenOccurrence(task.id, iso, traceId) : await api.completeOccurrence(task.id, iso, traceId);
            logDiag({ kind: "complete.response", task_id: task.id, iso, trace_id: traceId, state: stateSummary(upd) });
            setLocalCompletions(upd.completions || nextCompletions);
            setLocalCompletionTimes(upd.completion_times || nextCompletionTimes);
            onCompletionsChanged?.(task.id, upd.completions || nextCompletions, upd.completion_times || nextCompletionTimes);
        } catch {
            toast.error("Couldn't save — try again");
        } finally {
            setBusyIso(null);
        }
    };

    return (
        <Dialog open={!!task} onOpenChange={(open) => { if (!open) onClose?.(); }}>
            <DialogContent
                className="bg-meye-bg border-meye-border text-zinc-100 max-w-md"
                data-testid="missed-occurrences-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-brand tracking-widest uppercase text-sm text-meye-fuchsia">
                        Missed · {task.text}
                        {task.time && (
                            <span className="text-meye-cyan ml-2">{formatTime(task.time)}</span>
                        )}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Per-occurrence actions keyed by source date</DialogDescription>
                </DialogHeader>
                <p className="text-xs text-zinc-400 -mt-2">
                    {missed.length} outstanding · tap to mark that day done. Older misses stay
                    outstanding until you clear them.
                </p>
                <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
                    {missed.map((iso) => (
                        <button
                            key={iso}
                            onClick={() => toggle(iso, false)}
                            disabled={busyIso === iso}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-red-500/25 bg-red-500/5 hover:bg-red-500/15 text-left"
                            data-testid={`missed-occurrence-${iso}`}
                        >
                            <span className="w-5 h-5 rounded-md border border-zinc-600 flex items-center justify-center flex-shrink-0">
                                {busyIso === iso && <Loader2 size={12} className="animate-spin text-meye-cyan" />}
                            </span>
                            <span className="flex-1 text-sm font-medium">{isoToLabel(iso)}</span>
                            <span className="text-[10px] font-brand font-bold uppercase tracking-widest text-red-300">missed</span>
                        </button>
                    ))}
                    {missed.length === 0 && (
                        <p className="text-sm text-zinc-500 text-center py-6">
                            All missed occurrences cleared. Nice.
                        </p>
                    )}
                </div>
                {doneFromThisSession.length > 0 && (
                    <>
                        <p className="text-[10px] font-brand font-bold uppercase tracking-widest text-emerald-300 mt-2">
                            Cleared this session · tap to undo
                        </p>
                        <div className="space-y-1 max-h-[20vh] overflow-y-auto pr-1">
                            {doneFromThisSession.map((iso) => (
                                <button
                                    key={iso}
                                    onClick={() => toggle(iso, true)}
                                    disabled={busyIso === iso}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/15 text-left"
                                    data-testid={`cleared-occurrence-${iso}`}
                                >
                                    <span className="w-5 h-5 rounded-md bg-emerald-500 border border-emerald-500 flex items-center justify-center flex-shrink-0">
                                        {busyIso === iso ? (
                                            <Loader2 size={12} className="animate-spin text-black" />
                                        ) : (
                                            <Check size={12} strokeWidth={3} className="text-black" />
                                        )}
                                    </span>
                                    <span className="flex-1 text-sm font-medium line-through text-zinc-400">
                                        {isoToLabel(iso)}
                                    </span>
                                    <RotateCcw size={12} className="text-emerald-300" />
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MissedOccurrencesDialog;
